import { io } from 'socket.io-client';
import { observable, action } from 'mobx';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { IApiService } from '~/services/interfaces/IApiService';
import { WebsocketNamespace, getNamespaceParts } from '~/utils/websocket.util';
import { LoggerService } from '~/services/logger/logger.service';
import { IWebSocketsAdapter } from '~/services/interfaces/IWebSocketsAdapter';
import { IConfigurationProvider } from '~/types/dependency.types';
import { ISocket } from '~/services/interfaces/ISocket';

export interface SocketAuth {
    token: string;
}

// This class was created to address compatibility issues between @observable and the Socket class from socket.io-client.
// Changes within the socket instance do not automatically trigger observable updates in MobX
// As a result, changes in connection status were not properly tracked, leading to a race condition.
// The race condition occurred because some hooks registered to the socket before the connection was established, while others registered afterward.
class WebSocketsStatus {
    @observable isConnected: boolean;

    @action.bound
    markAsConnected() {
        this.isConnected = true;
    }

    @action.bound
    markAsDisconnected() {
        this.isConnected = false;
    }
}

export abstract class BaseWebSocketsAdapter
    extends BaseHttpAdapter
    implements IWebSocketsAdapter
{
    socket: ISocket;
    loggerService: LoggerService;
    configProvider: IConfigurationProvider;
    private webSocketStatus = new WebSocketsStatus();

    constructor({
        configProvider,
        apiService
    }: {
        configProvider: IConfigurationProvider;
        apiService: IApiService;
    }) {
        super(apiService);

        this.configProvider = configProvider;
        this.loggerService = new LoggerService({
            featureName: 'websocketAdapter',
            messagePrefix: '[WEBSOCKET_V3]'
        });
    }

    get isConnected() {
        return this.webSocketStatus.isConnected;
    }

    private get _shouldConnect() {
        const { config } = this.configProvider;

        return config.websocket_v3_address;
    }

    connect({ auth }: { auth: SocketAuth }) {
        const { config } = this.configProvider;

        if (!this._shouldConnect) {
            return false;
        }

        if (this.socket) {
            this.disconnect();

            this.socket.off('connect', this.webSocketStatus.markAsConnected);
            this.socket.off(
                'disconnect',
                this.webSocketStatus.markAsDisconnected
            );
        }

        this.socket = this.generateWebSocket({
            auth,
            address: config.websocket_v3_address,
            connectionRetry: config.web_socket_conn_retry,
            transports: this.configProvider.config.websocket_v3_transports
        });

        this.socket.on('connect', this.webSocketStatus.markAsConnected);
        this.socket.on(
            'connect_error',
            this.webSocketStatus.markAsDisconnected
        );
        this.socket.on('reconnect_attempt', attempt =>
            this.loggerService.logInfo('socket reconnect attempt', { attempt })
        );
        this.socket.on('error', () =>
            this.loggerService.logError('socket error')
        );
        this.socket.on('disconnect', this.webSocketStatus.markAsDisconnected);
        this.socket.onAny(event =>
            this.loggerService.logInfo(
                `socket incoming message`,
                {
                    event,
                    transport: this?.socket?.io?.engine?.transport?.name
                },
                0.02
            )
        );
        this.socket.onAnyOutgoing(event =>
            this.loggerService.logInfo(
                `socket outgoing message`,
                {
                    event,
                    transport: this?.socket?.io?.engine?.transport?.name
                },
                0.02
            )
        );

        return true;
    }

    disconnect() {
        if (!this.isConnected) {
            return;
        }

        this.socket.disconnect();
    }

    async getNamespaceToken(namespace: WebsocketNamespace, intent?: string) {
        const { namespaceName, namespaceId } = getNamespaceParts(namespace);

        const { token } = await this.apiService.apiRequest<{
            token: string;
        }>({
            method: 'GET',
            url: `/api/v2/websocket/auth/${namespaceName}/${namespaceId}?intent=${intent}`
        });

        return token;
    }

    abstract generateWebSocket(params: {
        auth: SocketAuth;
        address: string;
        connectionRetry?: boolean;
        transports?: string[];
    }): ISocket;
}

export class WebSocketsAdapter extends BaseWebSocketsAdapter {
    generateWebSocket(params: {
        auth: SocketAuth;
        address: string;
        connectionRetry?: boolean;
        transports: string[];
    }): ISocket {
        const { auth, address, connectionRetry, transports } = params;

        return io(address, {
            reconnection: connectionRetry,
            reconnectionDelay: 5000,
            reconnectionDelayMax: 60000,
            transports,
            auth
        });
    }
}
